import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

export interface MentionItem {
  id: string;
  /** Insert token (without leading "@"). E.g. "john.doe" → typed as "@john.doe". */
  handle: string;
  /** Primary line — usually the display name. */
  label: string;
  /** Optional secondary text (currently not rendered to keep rows compact). */
  sublabel?: string;
  /** Optional icon to render at the leading edge. */
  icon?: React.ReactNode;
  /** Optional accent class applied to the icon container. */
  accentClassName?: string;
}

export interface MentionPickerProps {
  /** Anchor point in viewport coordinates. Picker opens below; flips up if not enough room. */
  anchor: { top: number; left: number; lineHeight?: number } | null;
  /** Filter string (without leading "@"). Empty string = show all. */
  query: string;
  /** Suggestion list. */
  items: MentionItem[];
  /** Always shown as the first item, even if it doesn't match `query`. */
  pinnedFirst?: MentionItem;
  /** Maximum visible suggestions before scrolling (also caps render). */
  maxVisible?: number;
  onSelect: (item: MentionItem) => void;
  onClose: () => void;
}

const MENU_WIDTH = 240;
const ITEM_HEIGHT = 28;
const VIEWPORT_PADDING = 8;
const GAP_FROM_CARET = 4;

function scoreMatch(item: MentionItem, q: string): number {
  if (!q) return 1;
  const ql = q.toLowerCase();
  const fields = [item.handle, item.label, item.sublabel ?? ''];
  let best = 0;
  for (const f of fields) {
    const fl = f.toLowerCase();
    if (!fl) continue;
    if (fl === ql) best = Math.max(best, 4);
    else if (fl.startsWith(ql)) best = Math.max(best, 3);
    else if (fl.includes(ql)) best = Math.max(best, 2);
    else {
      const tokens = fl.split(/[\s._-]+/);
      if (tokens.some((t) => t.startsWith(ql))) best = Math.max(best, 1);
    }
  }
  return best;
}

const MentionPicker: React.FC<MentionPickerProps> = ({
  anchor,
  query,
  items,
  pinnedFirst,
  maxVisible = 6,
  onSelect,
  onClose,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [highlight, setHighlight] = useState(0);
  const [placement, setPlacement] = useState<{
    top: number;
    left: number;
    maxHeight: number | null;
    direction: 'down' | 'up';
  } | null>(null);

  const filtered = useMemo(() => {
    const seen = new Set<string>();
    if (pinnedFirst) seen.add(pinnedFirst.id);
    const matches = items
      .filter((i) => !seen.has(i.id))
      .map((i) => ({ i, s: scoreMatch(i, query) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s || a.i.label.localeCompare(b.i.label))
      .map((x) => x.i);
    return pinnedFirst ? [pinnedFirst, ...matches] : matches;
  }, [items, query, pinnedFirst]);

  useEffect(() => {
    setHighlight(0);
  }, [query, filtered.length]);

  const select = useCallback(
    (idx: number) => {
      const item = filtered[idx];
      if (item) onSelect(item);
    },
    [filtered, onSelect],
  );

  useEffect(() => {
    if (!anchor) return;
    const handler = (e: KeyboardEvent) => {
      if (filtered.length === 0 && e.key !== 'Escape') return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setHighlight((h) => (h + 1) % filtered.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setHighlight((h) => (h - 1 + filtered.length) % filtered.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        select(highlight);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [anchor, filtered.length, highlight, select, onClose]);

  useEffect(() => {
    if (!anchor) return;
    const onClickOutside = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [anchor, onClose]);

  // Measure actual rendered height and decide placement (down/up + maxHeight clamp).
  useLayoutEffect(() => {
    if (!anchor || !containerRef.current || filtered.length === 0) {
      setPlacement(null);
      return;
    }
    const el = containerRef.current;
    const lineHeight = anchor.lineHeight ?? 18;
    const naturalHeight = el.scrollHeight;
    const visibleCap = maxVisible * ITEM_HEIGHT + 8;

    const spaceBelow = window.innerHeight - (anchor.top + lineHeight) - VIEWPORT_PADDING - GAP_FROM_CARET;
    const spaceAbove = anchor.top - VIEWPORT_PADDING - GAP_FROM_CARET;

    const desiredHeight = Math.min(naturalHeight, visibleCap);
    const fitsBelow = desiredHeight <= spaceBelow;
    const direction: 'down' | 'up' = fitsBelow
      ? 'down'
      : spaceAbove > spaceBelow
        ? 'up'
        : 'down';

    const availableSpace = direction === 'down' ? spaceBelow : spaceAbove;
    const willScroll = desiredHeight > availableSpace || naturalHeight > visibleCap;
    const finalHeight = willScroll
      ? Math.max(ITEM_HEIGHT + 8, Math.min(desiredHeight, availableSpace))
      : naturalHeight;

    const left = Math.max(
      VIEWPORT_PADDING,
      Math.min(anchor.left, window.innerWidth - MENU_WIDTH - VIEWPORT_PADDING),
    );

    const top = direction === 'down'
      ? anchor.top + lineHeight + GAP_FROM_CARET
      : anchor.top - GAP_FROM_CARET - finalHeight;

    setPlacement({ top, left, maxHeight: willScroll ? finalHeight : null, direction });
  }, [anchor, filtered.length, maxVisible]);

  useEffect(() => {
    const el = itemRefs.current[highlight];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  if (!anchor || filtered.length === 0) return null;

  // Render with initial off-screen position; useLayoutEffect repositions before paint.
  const style: React.CSSProperties = placement
    ? {
        position: 'fixed',
        top: placement.top,
        left: placement.left,
        width: MENU_WIDTH,
        ...(placement.maxHeight != null
          ? { maxHeight: placement.maxHeight, overflowY: 'auto' as const }
          : { overflow: 'hidden' as const }),
      }
    : {
        position: 'fixed',
        top: -9999,
        left: -9999,
        width: MENU_WIDTH,
        visibility: 'hidden',
      };

  return createPortal(
    <div
      ref={containerRef}
      style={style}
      role="listbox"
      aria-label="Mention suggestions"
      className="z-tooltip py-1 rounded-lg bg-surface-page border border-default shadow-xl backdrop-blur-xl animate-scale-in"
    >
      {filtered.map((item, idx) => {
        const isPinned = pinnedFirst && item.id === pinnedFirst.id;
        const isActive = idx === highlight;
        return (
          <React.Fragment key={item.id}>
            <button
              ref={(el) => { itemRefs.current[idx] = el; }}
              type="button"
              role="option"
              aria-selected={isActive}
              onMouseEnter={() => setHighlight(idx)}
              onMouseDown={(e) => {
                e.preventDefault();
                select(idx);
              }}
              className={`w-full flex items-center gap-2 px-2.5 py-1 text-left border-none cursor-pointer transition-colors ${
                isActive ? 'bg-surface-secondary' : 'bg-transparent'
              }`}
            >
              <span
                className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center [&>svg]:size-3 ${
                  item.accentClassName ?? 'bg-surface-secondary text-content-muted'
                }`}
              >
                {item.icon}
              </span>
              <span className="min-w-0 flex-1 text-xs text-content truncate leading-tight">
                {item.label}
              </span>
              <span className="shrink-0 text-[10px] text-content-muted leading-tight">
                @{item.handle}
              </span>
            </button>
            {isPinned && filtered.length > 1 && (
              <div className="my-0.5 h-px bg-[var(--border-subtle)]" role="separator" />
            )}
          </React.Fragment>
        );
      })}
    </div>,
    document.body,
  );
};

export default MentionPicker;
